import React, { useEffect, useState } from 'react';
// Components
import Turno from './shift.jsx';
import Horario from './schedule.jsx';
// Context
import { ShiftsProvider, useShifts } from './ShiftsContext';
// Material-UI
import {
    Button,
    CircularProgress,
    IconButton,
    Modal,
    Snackbar,
    TextField,
} from '@material-ui/core';
// Icons
import { AlertTriangle, X } from 'tabler-icons-react';
// Styles
import '../../styles/turnos.css';
import '../../App.css';

const TurnosWrapper = () => (
    <ShiftsProvider>
        <Turnos />
    </ShiftsProvider>
);

export default TurnosWrapper;

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeInputToApi(val) {
    // "HH:MM" → "HH:MM:00"
    return val ? `${val}:00` : '';
}

function apiToTimeInput(val) {
    // "HH:MM:SS" or "1900-01-01THH:MM:SS" → "HH:MM"
    if (!val) return '';
    const t = val.includes('T') ? val.split('T')[1] : val;
    return t.slice(0, 5);
}

// ── Dialogs ───────────────────────────────────────────────────────────────────

function TurnoFormDialog({ open, onClose, lineId, isEdit }) {
    const { turns, editingTurn, crearTurno, updateTurno, saving } = useShifts();

    const target = isEdit ? editingTurn : null;
    const defaultTurnoNum = turns.length > 0 ? Math.max(...turns.map((t) => t.turno)) + 1 : 1;

    const [turnoNum, setTurnoNum] = useState(defaultTurnoNum);
    const [horaInicial, setHoraInicial] = useState('06:00');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            if (isEdit && target) {
                setTurnoNum(target.turno);
                setHoraInicial(apiToTimeInput(target.horaInicio));
            } else {
                setTurnoNum(defaultTurnoNum);
                setHoraInicial('06:00');
            }
            setErrors({});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const validate = () => {
        const e = {};
        if (!horaInicial) e.horaInicial = 'La hora de inicio es requerida';
        if (!isEdit) {
            if (!turnoNum || turnoNum < 1) e.turnoNum = 'Número de turno inválido';
            const exists = turns.some((t) => t.turno === Number(turnoNum));
            if (exists) e.turnoNum = `El turno ${turnoNum} ya existe`;
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        if (isEdit) {
            updateTurno(lineId, target.turno, timeInputToApi(horaInicial));
        } else {
            crearTurno(lineId, Number(turnoNum), timeInputToApi(horaInicial));
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <div className="turnos-modal">
                <div className="turnos-modal-header">
                    <h2 className="turnos-modal-title">
                        {isEdit ? `Editar Turno ${target?.turno}` : 'Nuevo turno'}
                    </h2>
                    <IconButton size="small" onClick={onClose}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>
                <div className="turnos-modal-body">
                    {!isEdit && (
                        <TextField
                            label="Número de turno"
                            type="number"
                            value={turnoNum}
                            onChange={(e) => setTurnoNum(e.target.value)}
                            error={!!errors.turnoNum}
                            helperText={errors.turnoNum}
                            inputProps={{ min: 1, max: 9 }}
                            variant="outlined"
                            size="small"
                            fullWidth
                        />
                    )}
                    <TextField
                        label="Hora de inicio"
                        type="time"
                        value={horaInicial}
                        onChange={(e) => setHoraInicial(e.target.value)}
                        error={!!errors.horaInicial}
                        helperText={errors.horaInicial}
                        inputProps={{ step: 60 }}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </div>
                <div className="turnos-modal-footer">
                    <Button onClick={onClose} disabled={saving} className="turnos-modal-cancel" variant="outlined">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        className="turnos-modal-submit"
                    >
                        {saving ? <CircularProgress size="1rem" /> : isEdit ? 'Guardar' : 'Crear'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function HorarioFormDialog({ open, onClose, lineId, turnId, isEdit }) {
    const { editingSchedule, crearHorario, updateHorario, saving } = useShifts();

    const target = isEdit ? editingSchedule : null;

    const [horaIni, setHoraIni] = useState('06:00');
    const [horaFin, setHoraFin] = useState('14:00');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            if (isEdit && target) {
                setHoraIni(apiToTimeInput(target.inicio));
                setHoraFin(apiToTimeInput(target.fin));
            } else {
                setHoraIni('06:00');
                setHoraFin('14:00');
            }
            setErrors({});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const validate = () => {
        const e = {};
        if (!horaIni) e.horaIni = 'La hora de inicio es requerida';
        if (!horaFin) e.horaFin = 'La hora de fin es requerida';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        if (isEdit) {
            updateHorario(lineId, turnId, target.segmento, timeInputToApi(horaIni), timeInputToApi(horaFin));
        } else {
            crearHorario(lineId, turnId, timeInputToApi(horaIni), timeInputToApi(horaFin));
        }
    };

    const crossesMidnight = horaFin && horaIni && horaFin <= horaIni;

    return (
        <Modal open={open} onClose={onClose}>
            <div className="turnos-modal">
                <div className="turnos-modal-header">
                    <h2 className="turnos-modal-title">
                        {isEdit ? `Editar Horario ${target?.segmento}` : 'Nuevo horario'}
                    </h2>
                    <IconButton size="small" onClick={onClose}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>
                <div className="turnos-modal-body">
                    <TextField
                        label="Hora de inicio"
                        type="time"
                        value={horaIni}
                        onChange={(e) => setHoraIni(e.target.value)}
                        error={!!errors.horaIni}
                        helperText={errors.horaIni}
                        inputProps={{ step: 60 }}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label="Hora de fin"
                        type="time"
                        value={horaFin}
                        onChange={(e) => setHoraFin(e.target.value)}
                        error={!!errors.horaFin}
                        helperText={errors.horaFin}
                        inputProps={{ step: 60 }}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                    {crossesMidnight && (
                        <div className="turnos-midnight-notice">
                            <AlertTriangle size={14} strokeWidth={1.75} />
                            El horario cruza la medianoche (termina el día siguiente)
                        </div>
                    )}
                </div>
                <div className="turnos-modal-footer">
                    <Button onClick={onClose} disabled={saving} className="turnos-modal-cancel" variant="outlined">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        className="turnos-modal-submit"
                    >
                        {saving ? <CircularProgress size="1rem" /> : isEdit ? 'Guardar' : 'Crear'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function ConfirmDeleteDialog({ open, onClose, title, description, onConfirm, saving }) {
    return (
        <Modal open={open} onClose={onClose}>
            <div className="turnos-modal">
                <div className="turnos-modal-header">
                    <h2 className="turnos-modal-title turnos-modal-title--danger">{title}</h2>
                    <IconButton size="small" onClick={onClose}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>
                <div className="turnos-modal-body">
                    <p className="turnos-modal-desc">{description}</p>
                </div>
                <div className="turnos-modal-footer">
                    <Button onClick={onClose} disabled={saving} className="turnos-modal-cancel" variant="outlined">
                        Cancelar
                    </Button>
                    <Button
                        onClick={onConfirm}
                        variant="contained"
                        disabled={saving}
                        className="turnos-modal-submit turnos-modal-submit--danger"
                    >
                        {saving ? <CircularProgress size="1rem" /> : 'Eliminar'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

// ── Main view ─────────────────────────────────────────────────────────────────

function Turnos() {
    const {
        lines,
        selectedLine, setSelectedLine,
        lineName, setLineName,
        turns,
        selectedTurn, setSelectedTurn,
        schedules,
        loadingTurns, loadingSchedules, saving,
        snackbar, closeSnackbar,
        dialogCreateTurn, setDialogCreateTurn,
        dialogEditTurn, setDialogEditTurn,
        dialogDeleteTurn, setDialogDeleteTurn,
        dialogCreateSchedule, setDialogCreateSchedule,
        dialogEditSchedule, setDialogEditSchedule,
        dialogDeleteSchedule, setDialogDeleteSchedule,
        deletingSchedule, setDeletingSchedule,
        getLines, getTurnos,
        borrarTurno, borrarHorario,
    } = useShifts();

    useEffect(() => {
        getLines();
    }, [getLines]);

    const handleLineClick = (item) => {
        setSelectedLine(item.id);
        setLineName(item.nombre);
        setSelectedTurn(null);
        getTurnos(item.id);
    };

    const handleCloseDeleteTurn = () => {
        setDialogDeleteTurn(false);
    };

    const handleConfirmDeleteTurn = () => {
        borrarTurno(selectedLine, selectedTurn.turno);
    };

    const handleCloseDeleteSchedule = () => {
        setDialogDeleteSchedule(false);
        setDeletingSchedule(null);
    };

    const handleConfirmDeleteSchedule = () => {
        borrarHorario(selectedLine, selectedTurn.turno, deletingSchedule.segmento);
    };

    return (
        <div className="turnos-page">
            {/* ── Header ── */}
            <div className="turnos-header">
                <div>
                    <h1 className="titulo">Turnos</h1>
                    <p className="desc">Gestiona los turnos y sus horarios por línea de producción</p>
                </div>
            </div>

            {/* ── Body ── */}
            <div className="turnos-body">
                {/* Lines panel */}
                <div className="turnos-panel-lines">
                    <div className="turnos-card">
                        <div className="turnos-card-toolbar">
                            <h2 className="turnos-card-title">Líneas</h2>
                        </div>
                        <div className="turnos-lines-list">
                            {lines.map((item) => (
                                <div
                                    key={item.id}
                                    className={`turnos-line-item${selectedLine === item.id ? ' turnos-line-item--selected' : ''}`}
                                    onClick={() => handleLineClick(item)}
                                >
                                    {item.nombre}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Main panel */}
                <div className="turnos-panel-main">
                    <div className="turnos-card">
                        <div className="turnos-card-toolbar">
                            <h2 className="turnos-card-title">{lineName || 'Selecciona una línea'}</h2>
                        </div>

                        <div className="turnos-card-body">
                            {/* Shifts column */}
                            <div className="turnos-shifts-col">
                                {selectedLine ? (
                                    <div key={selectedLine} className="turnos-shifts-animate">
                                        {loadingTurns ? (
                                            <div className="turnos-loading">
                                                <CircularProgress size="1.5rem" />
                                            </div>
                                        ) : (
                                            <>
                                                {turns.map((turn) => (
                                                    <Turno key={turn.turno} turn={turn} />
                                                ))}
                                                <div className="turnos-shift-add">
                                                    <button
                                                        className="turnos-add-btn"
                                                        onClick={() => setDialogCreateTurn(true)}
                                                    >
                                                        <span className="turnos-add-icon">+</span>
                                                        <span>Agregar turno</span>
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="turnos-empty">Selecciona una línea</div>
                                )}
                            </div>

                            {/* Schedules area */}
                            <div
                                key={`${selectedLine}-${selectedTurn?.turno}`}
                                className="turnos-schedules-area"
                            >
                                {!selectedTurn ? (
                                    <div className="turnos-empty">
                                        {selectedLine
                                            ? 'Selecciona un turno para ver sus horarios'
                                            : 'Selecciona una línea para comenzar'}
                                    </div>
                                ) : loadingSchedules ? (
                                    <div className="turnos-empty">
                                        <CircularProgress size="1.5rem" />
                                    </div>
                                ) : (
                                    <>
                                        {schedules.map((schedule, i) => (
                                            <Horario key={schedule.segmento} schedule={schedule} index={i} />
                                        ))}
                                        <div className="turnos-schedule-card turnos-schedule-add">
                                            <button
                                                className="turnos-add-btn"
                                                onClick={() => setDialogCreateSchedule(true)}
                                            >
                                                <span className="turnos-add-icon">+</span>
                                                <span>Agregar horario</span>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Dialogs ── */}
            <TurnoFormDialog
                open={dialogCreateTurn}
                onClose={() => setDialogCreateTurn(false)}
                lineId={selectedLine}
                isEdit={false}
            />
            <TurnoFormDialog
                open={dialogEditTurn}
                onClose={() => setDialogEditTurn(false)}
                lineId={selectedLine}
                isEdit
            />
            <ConfirmDeleteDialog
                open={dialogDeleteTurn}
                onClose={handleCloseDeleteTurn}
                title="Eliminar turno"
                description={`¿Estás seguro de eliminar el Turno ${selectedTurn?.turno}? Se eliminarán también sus horarios.`}
                onConfirm={handleConfirmDeleteTurn}
                saving={saving}
            />
            <HorarioFormDialog
                open={dialogCreateSchedule}
                onClose={() => setDialogCreateSchedule(false)}
                lineId={selectedLine}
                turnId={selectedTurn?.turno}
                isEdit={false}
            />
            <HorarioFormDialog
                open={dialogEditSchedule}
                onClose={() => setDialogEditSchedule(false)}
                lineId={selectedLine}
                turnId={selectedTurn?.turno}
                isEdit
            />
            <ConfirmDeleteDialog
                open={dialogDeleteSchedule}
                onClose={handleCloseDeleteSchedule}
                title="Eliminar horario"
                description={`¿Estás seguro de eliminar el Segmento ${deletingSchedule?.segmento}?`}
                onConfirm={handleConfirmDeleteSchedule}
                saving={saving}
            />

            {/* ── Snackbar ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <div className={`turnos-snackbar turnos-snackbar--${snackbar.severity}`}>
                    {snackbar.message}
                </div>
            </Snackbar>
        </div>
    );
}
